import { STOCK_STATE_LABELS, csvCell, type StockRecords } from "./stock.js";
import { flattenLocations, locationTree, pathLabel } from "./tree.js";
import { CONDITION_LABELS, STATUS_LABELS } from "./types.js";
import type { CategoryCount, Item, LocationCount } from "./types.js";

/**
 * A spreadsheet of some of what an inventory knows, for matching it up with a
 * system that lives somewhere else: an accounting package, an insurer's
 * schedule, a warehouse tool. The zip export (backup.ts) is "keep everything";
 * this is "give me these columns, named the way the other side wants them".
 *
 * Like the zip it is a member's own copy, on their own device, so it may carry
 * notes and private fields -- the picker marks them so nobody ticks one by
 * accident, but this file does not refuse them. It is not a third exit
 * alongside the prompt and the listing, and does not go through
 * `publicFields()` (docs/notes/invariants.md).
 *
 * Pure, so the node suite tests it and the browser builds the file without
 * another route.
 */

/** One column somebody may choose. `id` is what a saved choice refers to. */
export interface ItemCsvColumn {
  id: string;
  /** The default header, and what the picker shows. */
  label: string;
  /** Notes and private fields: the picker flags these and leaves them unticked. */
  private: boolean;
}

/** A chosen column, in the order it goes in the file, with the header the file will use. */
export interface ItemCsvChoice {
  id: string;
  header: string;
}

/** Custom fields are columns too, one per label; the id carries the label so a saved choice survives a reload. */
const FIELD_PREFIX = "field:";
const fieldKey = (label: string): string => label.trim().toLowerCase();

const day = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** Dollars and cents, no symbol and no thousands separator: what a spreadsheet import expects. */
const price = (cents: number | null): string => (cents === null ? "" : (cents / 100).toFixed(2));

interface Lookups {
  /** Location id to its full path, "Basement › Shelf 2". */
  place: (id: string | null) => string;
  category: (id: string | null) => string;
  /** A stockroom's facts about one item, or undefined where the item is not stock (or this is not a stockroom). */
  stock: (itemId: string) => StockFacts | undefined;
}

interface StockFacts {
  row: StockRecords["stock"][number];
  clientName: string;
  receipt: StockRecords["receipts"][number] | undefined;
}

const cents = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : price(value);

/**
 * A stockroom's columns, which exist only when the records were given. They
 * live in the Durable Object's stock tables rather than on `Item`, so they are
 * joined on the item id; an item that was never put on the shelf gets empty
 * cells. Cost and the vendor's invoice number are private, and offered as such.
 */
const STOCK_COLUMNS: (ItemCsvColumn & { value: (facts: StockFacts) => string })[] = [
  {
    id: "stock:state",
    label: "Stock state",
    private: false,
    value: ({ row }) => STOCK_STATE_LABELS[row.state],
  },
  { id: "stock:tracking", label: "Tracking", private: false, value: ({ row }) => row.tracking },
  { id: "stock:on_hand", label: "On hand", private: false, value: ({ row }) => String(row.onHand) },
  { id: "stock:deployed", label: "Deployed", private: false, value: ({ row }) => String(row.deployed) },
  { id: "stock:client", label: "At client", private: false, value: ({ clientName }) => clientName },
  { id: "stock:vendor", label: "Vendor", private: false, value: ({ receipt }) => receipt?.vendor ?? "" },
  {
    id: "stock:received_on",
    label: "Received on",
    private: false,
    value: ({ receipt }) => receipt?.receivedOn ?? "",
  },
  { id: "stock:unit_cost", label: "Unit cost", private: true, value: ({ row }) => cents(row.unitCostCents) },
  {
    id: "stock:vendor_invoice",
    label: "Vendor invoice",
    private: true,
    value: ({ receipt }) => receipt?.invoiceNumber ?? "",
  },
];

const BUILT_IN: (ItemCsvColumn & { value: (item: Item, lookups: Lookups) => string })[] = [
  { id: "id", label: "ID", private: false, value: (item) => item.id },
  { id: "title", label: "Title", private: false, value: (item) => item.title },
  { id: "description", label: "Description", private: false, value: (item) => item.description },
  { id: "location", label: "Location", private: false, value: (item, { place }) => place(item.locationId) },
  {
    id: "category",
    label: "Category",
    private: false,
    value: (item, { category }) => category(item.categoryId),
  },
  { id: "tags", label: "Tags", private: false, value: (item) => item.tags.join("; ") },
  { id: "quantity", label: "Quantity", private: false, value: (item) => String(item.quantity) },
  { id: "price", label: "Price", private: false, value: (item) => price(item.priceCents) },
  {
    id: "condition",
    label: "Condition",
    private: false,
    value: (item) => CONDITION_LABELS[item.condition],
  },
  { id: "status", label: "Status", private: false, value: (item) => STATUS_LABELS[item.status] },
  { id: "photos", label: "Photos", private: false, value: (item) => String(item.photos.length) },
  { id: "created", label: "Added on", private: false, value: (item) => day(item.createdAt) },
  { id: "updated", label: "Last changed", private: false, value: (item) => day(item.updatedAt) },
  { id: "notes", label: "Notes", private: true, value: (item) => item.notes },
];

/** The columns nobody has to earn: they exist for every item. Title and location lead, since that is what a match is usually on. */
export const ITEM_CSV_COLUMNS: readonly ItemCsvColumn[] = BUILT_IN.map(({ id, label, private: hidden }) => ({
  id,
  label,
  private: hidden,
}));

/** What to tick when nobody has chosen anything yet. */
export const ITEM_CSV_DEFAULT_IDS: readonly string[] = ["title", "location", "category", "quantity", "price"];

/**
 * Every column on offer for these items: the built-in ones, then one per
 * custom-field label found on any item (case-insensitively, so "Serial" and
 * "serial" are one column). A label that is private on any item is offered as
 * private, which errs toward the warning.
 */
export function itemCsvColumns(items: readonly Item[], withStock = false): ItemCsvColumn[] {
  const found = new Map<string, ItemCsvColumn>();
  for (const item of items) {
    for (const field of item.fields) {
      const key = fieldKey(field.label);
      if (!key) continue;
      const seen = found.get(key);
      if (seen) {
        if (field.visibility === "private") seen.private = true;
      } else {
        found.set(key, {
          id: `${FIELD_PREFIX}${key}`,
          label: field.label.trim(),
          private: field.visibility === "private",
        });
      }
    }
  }
  const fields = [...found.values()].sort((a, b) => a.label.localeCompare(b.label));
  const stock = withStock
    ? STOCK_COLUMNS.map(({ id, label, private: hidden }) => ({ id, label, private: hidden }))
    : [];
  return [...ITEM_CSV_COLUMNS, ...stock, ...fields];
}

/**
 * One row per item, in the given order, with the chosen columns.
 *
 * `stockRecords` (GET /api/stock/records) is given only for a stockroom, and
 * is what the `stock:` columns read; without it they are empty.
 *
 * A custom field an item does not have is an empty cell; one it has twice
 * under the same label joins with "; ", the way tags do. Text is defused
 * against formula injection by `csvCell`; the numeric columns are not text and
 * are left alone. CRLF line endings, the same as every other CSV here.
 */
export function buildItemsCsv(
  items: readonly Item[],
  choices: readonly ItemCsvChoice[],
  taxonomy: { locations: readonly LocationCount[]; categories: readonly CategoryCount[] },
  stockRecords?: StockRecords,
): string {
  const places = new Map(
    flattenLocations(locationTree([...taxonomy.locations])).map((node) => [node.id, pathLabel(node.path)]),
  );
  const categories = new Map(taxonomy.categories.map((category) => [category.id, category.name]));
  const stockRows = new Map((stockRecords?.stock ?? []).map((row) => [row.itemId, row]));
  const receipts = new Map((stockRecords?.receipts ?? []).map((receipt) => [receipt.id, receipt]));
  const clients = new Map((stockRecords?.clients ?? []).map((client) => [client.id, client.name]));
  const stockColumns = new Map(STOCK_COLUMNS.map((column) => [column.id, column]));
  const lookups: Lookups = {
    stock: (itemId) => {
      const row = stockRows.get(itemId);
      if (!row) return undefined;
      return {
        row,
        clientName: row.clientId ? (clients.get(row.clientId) ?? "") : "",
        receipt: row.receiptId ? receipts.get(row.receiptId) : undefined,
      };
    },
    place: (id) => (id === null ? "" : (places.get(id) ?? "")),
    category: (id) => (id === null ? "" : (categories.get(id) ?? "")),
  };

  const numeric = new Set(["quantity", "price", "photos"]);
  const builtIn = new Map(BUILT_IN.map((column) => [column.id, column]));

  const cell = (item: Item, choice: ItemCsvChoice): string => {
    const column = builtIn.get(choice.id);
    if (column) {
      const value = column.value(item, lookups);
      return numeric.has(column.id) ? value : csvCell(value);
    }
    const stockColumn = stockColumns.get(choice.id);
    if (stockColumn) {
      const facts = lookups.stock(item.id);
      if (!facts) return "";
      const value = stockColumn.value(facts);
      return ["stock:on_hand", "stock:deployed", "stock:unit_cost"].includes(choice.id)
        ? value
        : csvCell(value);
    }
    if (choice.id.startsWith(FIELD_PREFIX)) {
      const key = choice.id.slice(FIELD_PREFIX.length);
      return csvCell(
        item.fields
          .filter((field) => fieldKey(field.label) === key)
          .map((field) => field.value)
          .filter((value) => value !== "")
          .join("; "),
      );
    }
    return "";
  };

  const rows = [
    choices.map((choice) => csvCell(choice.header)).join(","),
    ...items.map((item) => choices.map((choice) => cell(item, choice)).join(",")),
  ];
  return `${rows.join("\r\n")}\r\n`;
}

/** The file's name: what it is, which inventory, and the day, so a second export does not overwrite the first. */
export const itemsCsvFileName = (inventoryId: string, now: Date = new Date()): string =>
  `utucat-items-${inventoryId.slice(0, 8)}-${now.toISOString().slice(0, 10)}.csv`;
